import { useState } from "react";
import { toast } from "sonner";
import { httpsCallable } from "firebase/functions";
import PageHero from "@/components/shared/PageHero";
import { functions } from "@/lib/firebase";
import { ASSETS, BRAND, SOCIALS } from "@/lib/brand";
import { ENQUIRY_TYPES } from "@/data/mockData";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Instagram, Facebook, Youtube, Link2, MessageCircle, Mail, Phone } from "lucide-react";

const initial = { name: "", email: "", phone: "", enquiry: "", message: "", company: "" };

const submitEnquiry = httpsCallable(functions, "submitEnquiry");

export default function ContactPage() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = (k) => (v) =>
    setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitEnquiry({
        name: form.name,
        email: form.email,
        phone: form.phone,
        enquiryType: form.enquiry,
        message: form.message,
        company: form.company, // honeypot (should be empty)
        sourcePage: typeof window !== "undefined" ? window.location.pathname : "",
      });
      setSubmitted(true);
      toast.success("Thank you — your message has been received.");
      setForm(initial);
    } catch (err) {
      const code = err?.code || "";
      if (code.includes("resource-exhausted")) {
        toast.error("You've sent a few messages already — please try again in a little while.");
      } else if (code.includes("invalid-argument")) {
        toast.error(err.message || "Please check your details and try again.");
      } else {
        toast.error("Something went wrong sending your message. Please try WhatsApp instead.");
      }
      // eslint-disable-next-line no-console
      console.error("[contact] submit failed:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <PageHero
        eyebrow="Contact"
        title={<>Connect <span className="italic text-bronze">With Me.</span></>}
        sub="Join a community dedicated to your property success. Follow me on social media for market updates, tips, and fresh listings."
        image={ASSETS.portraits.formal}
        ctas={[
          { label: "WhatsApp Kiap Yong", href: BRAND.whatsapp, variant: "primary" },
          { label: "Linktree", href: SOCIALS.linktree, variant: "secondary" },
        ]}
        meta={[
          { label: "Direct Line", value: BRAND.phoneDisplay },
          { label: "Reply Within", value: "1 business day" },
          { label: "Languages", value: "EN · 中文" },
        ]}
      />

      <section data-testid="contact-section" className="py-16 md:py-24 bg-cream">
        <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <h2 className="font-serif text-3xl md:text-4xl text-ink mb-2">Send a message</h2>
            <p className="text-sm text-olive">I read every enquiry personally and aim to reply within one business day.</p>

            <form onSubmit={handleSubmit} data-testid="contact-form" className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
              {/* Honeypot — hidden from real users; bots that fill it are dropped */}
              <input
                type="text"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                value={form.company}
                onChange={setField("company")}
                data-testid="contact-company"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              <label className="block md:col-span-1">
                <span className="editorial-label">Your Name</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={setField("name")}
                  data-testid="contact-name"
                  className="editorial-input"
                  placeholder="e.g. Jane Tan"
                />
              </label>
              <label className="block md:col-span-1">
                <span className="editorial-label">Email Address</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={setField("email")}
                  data-testid="contact-email"
                  className="editorial-input"
                  placeholder="you@email.com"
                />
              </label>
              <label className="block md:col-span-1">
                <span className="editorial-label">Phone Number</span>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={setField("phone")}
                  data-testid="contact-phone"
                  className="editorial-input"
                  placeholder="+65"
                />
              </label>
              <div className="block md:col-span-1">
                <span className="editorial-label">Enquiry Type</span>
                <Select value={form.enquiry} onValueChange={setField("enquiry")}>
                  <SelectTrigger
                    data-testid="contact-enquiry"
                    className="editorial-input flex justify-between border-0 border-b border-bronze/40 rounded-none focus:ring-0 px-0 h-auto py-3 bg-transparent"
                  >
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENQUIRY_TYPES.map((e) => (
                      <SelectItem key={e} value={e} data-testid={`contact-enquiry-opt-${e.slice(0,12).replace(/\s+/g,'-')}`}>
                        {e}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <label className="block md:col-span-2">
                <span className="editorial-label">Your Message</span>
                <textarea
                  value={form.message}
                  onChange={setField("message")}
                  data-testid="contact-message"
                  rows={5}
                  className="editorial-input resize-none"
                  placeholder="Tell me a little about what you're hoping to achieve..."
                />
              </label>
              <div className="md:col-span-2 flex flex-wrap gap-4 items-center">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="btn-primary disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send Message"}
                </button>
                {submitted && (
                  <span data-testid="contact-success" className="text-sm text-olive italic">
                    Received — I'll be in touch shortly. ✦
                  </span>
                )}
              </div>
            </form>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-5">
            <div className="border border-bronze/25 bg-beige/40 p-8 md:p-10">
              <h3 className="font-serif text-2xl text-ink">Direct Lines</h3>
              <ul className="mt-6 space-y-5 text-sm text-ink_soft/85">
                <li className="flex items-center gap-4">
                  <Phone size={16} className="text-bronze" />
                  <a href={BRAND.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contact-phone-link">
                    {BRAND.phoneDisplay}
                  </a>
                </li>
                <li className="flex items-center gap-4">
                  <Mail size={16} className="text-bronze" />
                  <a href={`mailto:${BRAND.email}`} data-testid="contact-email-link">
                    {BRAND.email}
                  </a>
                </li>
              </ul>

              <div className="mt-8 shimmer-line" />

              <h3 className="mt-8 font-serif text-2xl text-ink">Follow A Homely Abode</h3>
              <div className="mt-5 flex items-center gap-3 flex-wrap">
                {[
                  { href: SOCIALS.instagram, Icon: Instagram, label: "Instagram" },
                  { href: SOCIALS.facebook, Icon: Facebook, label: "Facebook" },
                  { href: SOCIALS.youtube, Icon: Youtube, label: "YouTube" },
                  { href: SOCIALS.linktree, Icon: Link2, label: "Linktree" },
                ].map(({ href, Icon, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={`contact-social-${label.toLowerCase()}`}
                    className="inline-flex h-11 w-11 items-center justify-center border border-bronze/40 text-ink hover:bg-bronze hover:text-cream hover:border-bronze transition-colors"
                  >
                    <Icon size={16} />
                  </a>
                ))}
                <a
                  href={SOCIALS.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                  data-testid="contact-social-tiktok"
                  className="inline-flex h-11 w-11 items-center justify-center border border-bronze/40 text-ink hover:bg-bronze hover:text-cream hover:border-bronze transition-colors"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.5a8.16 8.16 0 0 0 4.77 1.52V6.69h-1.84z" />
                  </svg>
                </a>
              </div>

              <div className="mt-8 flex flex-col gap-3">
                <a
                  href={BRAND.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-cta-whatsapp"
                  className="btn-primary"
                >
                  <MessageCircle size={14} /> WhatsApp Kiap Yong
                </a>
                <a
                  href={SOCIALS.linktree}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-cta-linktree"
                  className="btn-secondary"
                >
                  <Link2 size={14} /> Open Linktree
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
